/* EP START: delivery-countdown v3.1, 29 Oct 2026.
   v3.1: Saturday is a delivery day. Royal Mail Tracked 24 delivers Monday to Saturday, and our own tracking
   (12 Fridays, 7 Aug to 23 Oct) shows 84% of parcels shipped on a Friday arrive on the Saturday. Before this,
   an order shipping Friday (Thursday after 4pm, Friday before 4pm) was promised for Monday. Dispatch days are
   still Monday to Friday, so Friday after 4pm and the weekend still say Tuesday, which is when they arrive.
   Sunday and bank holidays are never delivery days.
   v3.0, 28 Oct 2026:
   After the 4pm cut-off the old line said "Free next working day delivery, order now for delivery [date]",
   which read like "you just missed it". 4 to 6pm weekday conversion was 2.18% against 2.85% the rest of the day.
   The before-4pm countdown is unchanged: its urgency comes from a real deadline.
   After 4pm there is no real deadline until tomorrow's cut-off, so v3.0 uses the one real source of urgency
   left, low stock, and otherwise says plainly when the order arrives:
   1. [stock] gives "Only 3 left. " when the selected variant has 1 to 5 in stock (tracked, not preorder).
      Counts come from templates/product.ep-stock.liquid (?view=ep-stock), capped at 6 so big counts stay private.
   2. The arrival date is in bold.
   3. [free] gives ", free" at or above the GBP 49.98 free delivery threshold, nothing below it.
   4. [weekend] adds "and in time for the weekend" when the order lands on a Thursday or Friday.
   5. [dispatch] still works ("tomorrow" or a day name) for older strings.
   6. "Free delivery" is dropped from the countdown on products under GBP 49.98.
   Strings live in locales/en.default.json, products.product.delivery_countdown. Older strings still work.
   v2.0 (same day) led with "Free delivery [date]"; Ewan found it no stronger than the old line.
   EP END */
if (!customElements.get('delivery-countdown')) {
  const CUTOFF_HOUR = 16;
  const DAY_MS = 86400000;
  /* EP START: wording pieces and thresholds */
  const FREE_FROM = 49.98;
  const LOW_STOCK = 5;
  const FREE_TEXT = ', free';
  const WEEKEND_AFTER_FREE = ' and in time for the weekend';
  const WEEKEND_NO_FREE = ', in time for the weekend';
  const STOCK_ONE = 'Only 1 left. ';
  const STOCK_MANY = 'Only [n] left. ';
  /* EP END */

  const HOLIDAYS_URL = 'https://www.gov.uk/bank-holidays.json';
  const HOLIDAYS_STORAGE_KEY = 'delivery-countdown-bank-holidays';
  const HOLIDAYS_CACHE_MS = 7 * DAY_MS;

  const BANK_HOLIDAY_SEED = new Set([
    '2026-08-31',
    '2026-12-25',
    '2026-12-28',
    '2027-01-01',
    '2027-03-26',
    '2027-03-29',
    '2027-05-03',
    '2027-05-31',
    '2027-08-30',
    '2027-12-27',
    '2027-12-28',
    '2028-01-03',
    '2028-04-14',
    '2028-04-17',
    '2028-05-01',
    '2028-05-29',
    '2028-08-28',
    '2028-12-25',
    '2028-12-26',
  ]);

  let bankHolidays = BANK_HOLIDAY_SEED;
  let holidaysPromise = null;

  const readHolidaysCache = () => {
    try {
      const cache = JSON.parse(localStorage.getItem(HOLIDAYS_STORAGE_KEY));
      if (!Array.isArray(cache.dates) || !cache.dates.length) return null;
      if (Date.now() - cache.savedAt > HOLIDAYS_CACHE_MS) return null;
      return cache.dates;
    } catch {
      return null;
    }
  };

  const writeHolidaysCache = (dates) => {
    try {
      localStorage.setItem(HOLIDAYS_STORAGE_KEY, JSON.stringify({ savedAt: Date.now(), dates }));
    } catch {}
  };

  const loadBankHolidays = () => {
    holidaysPromise =
      holidaysPromise ||
      (async () => {
        const cached = readHolidaysCache();
        if (cached) {
          bankHolidays = new Set(cached);
          return;
        }
        const response = await fetch(HOLIDAYS_URL);
        if (!response.ok) return;
        const data = await response.json();
        const dates = data['england-and-wales'].events.map((event) => event.date);
        if (!dates.length) return;
        bankHolidays = new Set(dates);
        writeHolidaysCache(dates);
      })().catch(() => {});
    return holidaysPromise;
  };

  const londonClock = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hourCycle: 'h23',
  });

  const pageLang = document.documentElement.lang;
  const dateLocale = pageLang && pageLang !== 'en' ? pageLang : 'en-GB';

  const dateLabel = new Intl.DateTimeFormat(dateLocale, {
    timeZone: 'UTC',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  /* EP START: day name for [dispatch] */
  const dayLabel = new Intl.DateTimeFormat(dateLocale, { timeZone: 'UTC', weekday: 'long' });
  /* EP END */

  const londonNow = () => {
    const parts = {};
    for (const { type, value } of londonClock.formatToParts(new Date())) {
      if (type !== 'literal') parts[type] = Number(value);
    }
    return parts;
  };

  const isWorkingDay = (dayMs) => {
    const day = new Date(dayMs);
    const weekday = day.getUTCDay();
    return weekday >= 1 && weekday <= 5 && !bankHolidays.has(day.toISOString().slice(0, 10));
  };

  const nextWorkingDay = (dayMs) => {
    let day = dayMs + DAY_MS;
    while (!isWorkingDay(day)) day += DAY_MS;
    return day;
  };

  /* EP START: v3.1 delivery days: Monday to Saturday, not bank holidays (Royal Mail Tracked 24) */
  const isDeliveryDay = (dayMs) => {
    const day = new Date(dayMs);
    const md = day.toISOString().slice(5, 10);
    /* no Royal Mail deliveries on Christmas Day or Boxing Day, even when the bank holiday moves to a weekday */
    if (md === '12-25' || md === '12-26') return false;
    return day.getUTCDay() !== 0 && !bankHolidays.has(day.toISOString().slice(0, 10));
  };

  const nextDeliveryDay = (dayMs) => {
    let day = dayMs + DAY_MS;
    while (!isDeliveryDay(day)) day += DAY_MS;
    return day;
  };
  /* EP END */

  /* EP START: price check for the free delivery wording. No price found means leave "free" in. */
  const productPrice = () => {
    const meta = document.querySelector('meta[property="og:price:amount"]');
    const value = meta ? parseFloat(String(meta.content).replace(/,/g, '')) : NaN;
    return Number.isFinite(value) ? value : null;
  };

  const dropFree = (text) =>
    text.replace(/\b([Ff])ree delivery/g, (match, first) => (first === 'F' ? 'Delivery' : 'delivery'));
  /* EP END */

  /* EP START: low stock counts for [stock], loaded once per page from the ep-stock view */
  let stockMap = null;
  let stockPromise = null;
  const loadStock = () => {
    if (stockPromise) return stockPromise;
    const path = location.pathname;
    if (path.indexOf('/products/') === -1) return (stockPromise = Promise.resolve());
    stockPromise = fetch(path.replace(/\/$/, '') + '?view=ep-stock', { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.text() : ''))
      .then((text) => {
        const data = JSON.parse(text.trim());
        if (data && data.v) stockMap = data.v;
      })
      .catch(() => {});
    return stockPromise;
  };

  const selectedVariantId = () => {
    const input =
      document.querySelector('form.main-product-form [name="id"]') ||
      document.querySelector('form[action*="/cart/add"] [name="id"]');
    return input ? String(input.value) : null;
  };

  const stockText = () => {
    if (!stockMap) return '';
    const id = selectedVariantId();
    const count = id && Object.prototype.hasOwnProperty.call(stockMap, id) ? stockMap[id] : null;
    if (typeof count !== 'number' || count < 1 || count > LOW_STOCK) return '';
    return count === 1 ? STOCK_ONE : STOCK_MANY.replace('[n]', count);
  };
  /* EP END */

  class DeliveryCountdown extends HTMLElement {
    connectedCallback() {
      const { textCountdownTomorrow, textCountdown, textAfterCutoff, textWeekend } = this.dataset;
      if (!textCountdownTomorrow || !textCountdown || !textAfterCutoff || !textWeekend) return;

      this.output = this.querySelector('p') || this;
      this.onVisibilityChange = () => {
        if (!document.hidden) this.render();
      };

      this.render();
      loadBankHolidays().then(() => {
        if (this.isConnected) this.render();
      });
      /* EP START: stock counts, and re-render when another option is picked */
      loadStock().then(() => {
        if (this.isConnected) this.render();
      });
      this.onOptionChange = () => setTimeout(() => this.isConnected && this.render(), 60);
      document.addEventListener('change', this.onOptionChange);
      /* EP END */
      this.timer = setInterval(() => this.render(), 30000);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('change', this.onOptionChange);
    }

    render() {
      const now = londonNow();
      const today = Date.UTC(now.year, now.month - 1, now.day);
      const weekday = new Date(today).getUTCDay();
      const secondsToCutoff = CUTOFF_HOUR * 3600 - (now.hour * 3600 + now.minute * 60 + now.second);
      const beforeCutoff = secondsToCutoff > 0;

      let template;
      let delivery;
      let dispatch = null;

      if (isWorkingDay(today) && beforeCutoff) {
        delivery = nextDeliveryDay(today);
        const minutesLeft = Math.ceil(secondsToCutoff / 60);
        const hours = Math.floor(minutesLeft / 60);
        const minutes = minutesLeft % 60;
        const time = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
        const base = delivery - today === DAY_MS ? this.dataset.textCountdownTomorrow : this.dataset.textCountdown;
        template = base.replace('[time]', time);
      } else {
        dispatch = nextWorkingDay(today);
        delivery = nextDeliveryDay(dispatch);
        const inWeekendWindow = weekday === 0 || weekday === 6 || (weekday === 5 && !beforeCutoff);
        template = inWeekendWindow ? this.dataset.textWeekend : this.dataset.textAfterCutoff;
      }

      /* EP START: v3.0 tokens, free wording and the bold date */
      const price = productPrice();
      const isFree = price === null || price >= FREE_FROM;
      const deliveryDay = new Date(delivery).getUTCDay();
      const inTimeForWeekend = deliveryDay === 4 || deliveryDay === 5;
      const dispatchText = dispatch === null ? '' : dispatch - today === DAY_MS ? 'tomorrow' : dayLabel.format(dispatch);
      const stock = dispatch === null ? '' : stockText();
      template = template
        .replace('[stock]', stock ? '[stockmark]' : '')
        .replace('[free]', isFree ? FREE_TEXT : '')
        .replace('[weekend]', inTimeForWeekend ? (isFree ? WEEKEND_AFTER_FREE : WEEKEND_NO_FREE) : '')
        .replace('[dispatch]', dispatchText);

      if (!isFree) template = dropFree(template);

      const dateText = dateLabel.format(delivery);
      if (dispatch === null) {
        this.output.textContent = template.replace('[date]', dateText);
        return;
      }

      const nodes = template.split(/(\[date\]|\[stockmark\])/).map((part) => {
        if (part !== '[date]' && part !== '[stockmark]') return part;
        const strong = document.createElement('strong');
        if (part === '[date]') {
          strong.className = 'ep-dc-date';
          strong.style.cssText = 'font-weight:500;color:#0b1f44';
          strong.textContent = dateText;
        } else {
          strong.className = 'ep-dc-stock';
          strong.style.cssText = 'font-weight:500;color:#ce1c1f';
          strong.textContent = stock.trimEnd();
          return [strong, stock.slice(stock.trimEnd().length)];
        }
        return strong;
      });
      this.output.textContent = '';
      this.output.append(...nodes.flat().filter((node) => node !== ''));
      /* EP END */
    }
  }

  customElements.define('delivery-countdown', DeliveryCountdown);
}
